import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import FormPage from './components/FormPage';
import ConfigPage from './components/ConfigPage';
import DocumentsPage from './components/DocumentsPage';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import OfflineIndicator from './components/OfflineIndicator';

function App() {
  const [config, setConfig] = useState(() => {
    const saved = localStorage.getItem('uniformApp_config');
    return saved ? JSON.parse(saved) : {
      clientName: '',
      clientLogo: '',
      garmentTypes: [], // Sin prendas predeterminadas
      measurers: []
    };
  });
  
  const [formData, setFormData] = useState(() => {
    const saved = localStorage.getItem('uniformApp_formData');
    return saved ? JSON.parse(saved) : {
      name: '',
      group: '',
      location: '',
      lockerNumber: '',
      garments: {},
      pantsLength: '',
      interestedName: '',
      interestedSignature: '',
      date: new Date().toISOString().split('T')[0],
      selectedMeasurerId: null
    };
  });
  
  const [documents, setDocuments] = useState(() => {
    const saved = localStorage.getItem('uniformApp_documents');
    return saved ? JSON.parse(saved) : [];
  });
  
  const [usersWithGroups, setUsersWithGroups] = useState([]);
  const [editingDocumentId, setEditingDocumentId] = useState(null);

  // Cargar usuarios desde localStorage al iniciar
  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = () => {
    // Obtener usuarios del archivo importado si existen
    const importedData = localStorage.getItem('uniformApp_users_from_file');
    let usersList = [];
    if (importedData) {
      const data = JSON.parse(importedData);
      usersList = data.data || [];
    }
    setUsersWithGroups(usersList);
  };

  // Escuchar cambios en localStorage para actualizar la lista de usuarios
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'uniformApp_users_from_file') {
        loadUsers();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    
    // También escuchar cambios en el mismo dominio
    const originalSetItem = localStorage.setItem;
    localStorage.setItem = function(key, value) {
      originalSetItem.apply(this, arguments);
      if (key === 'uniformApp_users_from_file') {
        setTimeout(loadUsers, 0);
      }
    };
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      localStorage.setItem = originalSetItem;
    };
  }, []);

  useEffect(() => {
    localStorage.setItem('uniformApp_config', JSON.stringify(config));
  }, [config]);

  useEffect(() => {
    localStorage.setItem('uniformApp_formData', JSON.stringify(formData));
  }, [formData]);

  useEffect(() => {
    localStorage.setItem('uniformApp_documents', JSON.stringify(documents));
  }, [documents]);

  const updateConfig = (newConfig) => {
    setConfig({ ...config, ...newConfig });
  };

  const updateFormData = (newData) => {
    setFormData({ ...formData, ...newData });
  };

  const updateInterestedName = (name) => {
    setFormData({ ...formData, interestedName: name });
  };

  const clearFormData = () => {
    setFormData({
      name: '',
      group: '',
      location: '',
      lockerNumber: '',
      garments: {},
      pantsLength: '',
      interestedName: '',
      interestedSignature: '',
      date: new Date().toISOString().split('T')[0],
      selectedMeasurerId: null
    });
    setEditingDocumentId(null);
  };

  const cancelEdit = () => {
    clearFormData();
  };

  const saveDocument = () => {
    const selectedMeasurer = (config.measurers || []).find(m => m.id === formData.selectedMeasurerId) || (config.measurers || [])[0];
    if (editingDocumentId) {
      // Actualizar documento existente
      const updatedDocuments = documents.map(doc =>
        doc.id === editingDocumentId 
          ? {
              ...doc,
              ...formData,
              measurerName: selectedMeasurer?.name || '',
              measurerSignature: selectedMeasurer?.signature || '',
              config: { ...config },
              updatedAt: new Date().toISOString()
            } 
          : doc
      );
      setDocuments(updatedDocuments);
      const docId = editingDocumentId;
      clearFormData();
      setEditingDocumentId(null);
      alert(`Documento actualizado con ID: ${docId}`);
      return docId;
    } else {
      // Crear nuevo documento
      const newDoc = {
        id: Date.now(),
        ...formData,
        measurerName: selectedMeasurer?.name || '',
        measurerSignature: selectedMeasurer?.signature || '',
        config: { ...config },
        createdAt: new Date().toISOString()
      };
      setDocuments([...documents, newDoc]);
      clearFormData();
      return newDoc.id;
    }
  };

  const loadDocumentForEditing = (doc) => {
    // Cargar los datos del documento en el formulario
    setFormData({
      name: doc.name || '',
      group: doc.group || '',
      location: doc.location || '',
      lockerNumber: doc.lockerNumber || '',
      garments: doc.garments || {},
      pantsLength: doc.pantsLength || '',
      interestedName: doc.interestedName || '',
      interestedSignature: doc.interestedSignature || '',
      date: doc.date || new Date().toISOString().split('T')[0],
      selectedMeasurerId: doc.selectedMeasurerId || null
    });
    setEditingDocumentId(doc.id);
  };

  const deleteDocument = (id) => {
    console.log('deleteDocument llamado con ID:', id); // Debug
    console.log('Documentos antes de eliminar:', documents.length); // Debug
    const updatedDocuments = documents.filter(doc => doc.id !== id);
    console.log('Documentos después de eliminar:', updatedDocuments.length); // Debug
    setDocuments(updatedDocuments);
  };

  const clearAllData = () => {
    // Limpiar todos los datos almacenados en localStorage
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('uniformApp_')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    // Resetear el estado de la aplicación
    setConfig({
      clientName: '',
      clientLogo: '',
      garmentTypes: [], // Sin prendas predeterminadas
      measurers: []
    });
    clearFormData();
    setDocuments([]);
    setUsersWithGroups([]);
    setEditingDocumentId(null);
    
    // Recargar la página para asegurar que todo se reinicie
    window.location.reload();
  };

  // Verificar si la configuración está completa
  const isConfigurationComplete = config.clientName && config.garmentTypes && config.garmentTypes.length > 0;

  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
        <Routes>
          <Route element={<Layout config={config} />}>
            {/* Redirigir a configuración si no está completa */}
            <Route 
              path="/" 
              element={
                isConfigurationComplete ? (
                  <FormPage 
                    config={config} 
                    formData={formData} 
                    updateFormData={updateFormData} 
                    updateInterestedName={updateInterestedName} 
                    saveDocument={saveDocument} 
                    usersWithGroups={usersWithGroups}
                    isEditing={!!editingDocumentId}
                    cancelEdit={cancelEdit}
                  />
                ) : (
                  <Navigate to="/config" replace />
                )
              } 
            />
            <Route 
              path="/config" 
              element={
                <ConfigPage 
                  config={config} 
                  updateConfig={updateConfig} 
                  clearAllData={clearAllData} 
                  usersWithGroups={usersWithGroups}
                  onUsersUpdate={loadUsers}
                />
              } 
            />
            <Route 
              path="/documents" 
              element={
                isConfigurationComplete ? (
                  <DocumentsPage 
                    documents={documents} 
                    deleteDocument={deleteDocument} 
                    onLoadDocument={loadDocumentForEditing} 
                  />
                ) : (
                  <Navigate to="/config" replace />
                )
              } 
            />
          </Route>
        </Routes>
        
        {/* PWA Components */}
        <PWAInstallPrompt />
        <OfflineIndicator />
      </div>
    </Router>
  );
}

export default App;